import { Button } from "design-system/atoms/Button"
import { FlaskConical, MessagesSquare } from "lucide-react"
import { useStartTestingConversationMutation } from "modules/conversations/hooks/useStartTestingConversationMutation"
import { useTestingExperimentOptionsQuery } from "modules/conversations/hooks/useTestingExperimentOptionsQuery"
import { useDeveloperMode } from "modules/shared/hooks/useDeveloperMode"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { ConversationResponseDataSchema, CustomerResponseDataSchema, DataResponse } from "schema"
import z from "zod"
import { TestingExperiment, TestingExperimentSelect } from "./TestingExperimentSelect"

type Props = {
  customer: z.infer<typeof CustomerResponseDataSchema>
  campaignId?: string
  onTestingConversationStarted?: (
    response: DataResponse<z.infer<typeof ConversationResponseDataSchema>>,
  ) => unknown
}

export function EmptyState({ customer, campaignId, onTestingConversationStarted }: Props) {
  const { t } = useTranslation()
  const { developerMode } = useDeveloperMode()
  const [experiment, setExperiment] = useState<TestingExperiment | null>(null)
  const {
    data: experimentOptions = [],
    isLoading: isOptionsLoading,
    isError: isOptionsError,
  } = useTestingExperimentOptionsQuery(developerMode && customer.isFake && !!campaignId)

  const { mutateAsync: startTestingConversation, isPending: isStartingTestingConversation } =
    useStartTestingConversationMutation({
      onSuccess: onTestingConversationStarted,
    })

  if (customer.isFake && campaignId && onTestingConversationStarted) {
    return (
      <div className="flex flex-col lg:flex-row flex-1 min-h-0 w-full">
        <div className="flex flex-1 flex-col gap-8 px-4 py-12 items-center justify-center text-center">
          <MessagesSquare className="size-14 text-muted-foreground" strokeWidth={1.5} />
          <div className="flex flex-col gap-2">
            <span className="text-lg">{t("conversations.chat.noConversations.title")}</span>
            <span className="text-sm text-muted-foreground">
              {t("conversations.chat.noConversations.description")}
            </span>
          </div>
          <Button
            variant="testing"
            onClick={() =>
              startTestingConversation({
                customerId: customer.id,
                campaignId,
                experiment: developerMode ? experiment : undefined,
              })
            }
            disabled={
              isStartingTestingConversation ||
              (developerMode && (isOptionsLoading || isOptionsError))
            }
          >
            <FlaskConical className="size-4" />
            <span>{t("conversations.chat.noConversations.createTestingConversation")}</span>
          </Button>
        </div>
        {developerMode && experimentOptions.length > 0 && (
          <div className="w-full lg:w-sm shrink-0 lg:border-l border-border p-4">
            <TestingExperimentSelect
              options={experimentOptions}
              value={experiment}
              onChange={setExperiment}
              disabled={isStartingTestingConversation}
            />
          </div>
        )}
        {developerMode && isOptionsError && (
          <span className="text-sm text-destructive">{t("shared.error.description")}</span>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-8 px-4 py-12 items-center justify-center text-center max-w-md mx-auto">
      <MessagesSquare className="size-14 text-muted-foreground" strokeWidth={1.5} />
      <div className="flex flex-col gap-2">
        <span className="text-lg">{t("conversations.chat.noConversations.title")}</span>
        <span className="text-sm text-muted-foreground">
          {t("conversations.chat.noConversations.description")}
        </span>
      </div>
    </div>
  )
}
